import React from 'react';
import PropTypes from 'prop-types';

import styled, { ThemeProvider } from 'styled-components';
import { ReactSortable } from 'react-sortablejs';
import AdminSidebar from './AdminSidebar';
import { WIDGET_LIST } from '../widgets';
import randomString from './helpers/randomString';
import AdminWidget from './AdminWidget';
import ReorderWidgetsDialog from './dialogs/ReorderWidgetsDialog';
import PageSettingsDialog from './dialogs/PageSettingsDialog';
import theme from './raketa-ui/defaultTheme';
import Title from './raketa-ui/components/Title';
import Text from './raketa-ui/components/Text';

const EmptyCanvas = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const Loading = styled.div`
  display: ${props => props.$active ? 'block' : 'none'};
  position: fixed;
  right: 16px;
  top: 16px;
  padding: 8px 16px;
  background-color: rgba(0,0,0,.6);
  color: #fff;
  border-radius: 3px;
  z-index: 9999;
  backdrop-filter: blur(10px);
`;

class PageBuilder extends React.Component {
  static widgetFactory(widgetName) {
    const widget = WIDGET_LIST[widgetName];

    return {
      widgetId: randomString(6),
      component: widgetName,
      settings: widget.defaults,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      page: this.props.page,
      lastSaved: this.props.page,
      sitePreview: false,
      orderDialogOpen: false,
      pageSettingsOpen: false,
    };
  }

  componentDidMount() {
    this.checkAutosave();

    // Check for changes and warn user
    window.addEventListener('beforeunload', (event) => {
      if (JSON.stringify(this.state.page) !== JSON.stringify(this.state.lastSaved)) {
        event.preventDefault();
      }
    });
  }

  autosave() {
    if (typeof window !== 'undefined') {
      const { page } = this.state;
      const currentdate = new Date();
      const datetime = `${currentdate.getDate()}/${currentdate.getMonth() + 1}/${currentdate.getFullYear()} ${currentdate.getHours()}:${currentdate.getMinutes()}`;

      window.localStorage.setItem(`autosave-page-${page.id}`, JSON.stringify(page));
      window.localStorage.setItem(`autosave-date-${page.id}`, datetime);
      console.log('Autosaving...');
    }
  }

  checkAutosave() {
    if (typeof window !== 'undefined') {
      const { page } = this.state;
      const lastAutosave = window.localStorage.getItem(`autosave-page-${page.id}`);

      if (lastAutosave !== null && lastAutosave !== JSON.stringify(page)) {
        const autosaveDate = window.localStorage.getItem(`autosave-date-${page.id}`);

        if (confirm(`Auto-save from ${autosaveDate} found. Restore from it?`)) {
          this.setState({ page: JSON.parse(lastAutosave) });
        }
      }
    }
  }

  handleUpdate(widgetId, settings) {
    const { page } = this.state;
    const widgetIndex = page.widgets.findIndex(currentWidget =>
        currentWidget.widgetId === widgetId);

    const widgets = [
      ...page.widgets.slice(0, widgetIndex),
      Object.assign({}, page.widgets[widgetIndex], { settings }),
      ...page.widgets.slice(widgetIndex + 1),
    ];

    this.setState({ page: Object.assign({}, page, { widgets }) }, () => this.autosave());
  }

  handleDelete(widgetId) {
    const { page } = this.state;
    const widgetIndex = page.widgets.findIndex(currentWidget =>
      currentWidget.widgetId === widgetId);

    const widgets = [
      ...page.widgets.slice(0, widgetIndex),
      ...page.widgets.slice(widgetIndex + 1),
    ];

    this.setState({ page: Object.assign({}, page, { widgets }) }, () => this.autosave());
  }

  handleSave() {
    const { page } = this.state;
    const { save_url } = this.props;
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    this.setState({ isLoading: true }, () => {
      fetch(save_url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ widgets: page.widgets, page }),
      })
      .then(response => {
        if (!response.ok) throw new Error('Save failed');
      })
      .then(() => {
        this.setState({ isLoading: false, lastSaved: page });
        if (typeof window !== 'undefined') window.localStorage.removeItem(`autosave-page-${page.id}`);
      })
      .catch(() => {
        alert('An error occured saving this page. ');
      });
    });
  }

  handleAddWidget(widgetName) {
    if (widgetName === '') return;

    const { page } = this.state;

    const widgets = [
      ...page.widgets,
      PageBuilder.widgetFactory(widgetName),
    ];

    this.setState({ page: Object.assign({}, page, { widgets }) }, () => this.autosave());
  }

  handleReorder(newWidgets) {
    const { page } = this.state;
    const widgets = newWidgets.map(w => page.widgets.find(pw => pw.widgetId === (w.id || w.widgetId)) || w);
    this.setState({ page: Object.assign({}, page, { widgets }) }, () => this.autosave());
  }

  handleSitePreview() {
    this.setState({ sitePreview: !this.state.sitePreview });
  }

  handleReorderDialogOpen() {
    this.setState({ orderDialogOpen: true });
  }

  handlePageSettingsOpen() {
    this.setState({ pageSettingsOpen: true });
  }

  handleUpdateSettings(settings) {
    const { page } = this.state;
    this.setState({ page: Object.assign({}, page, settings), pageSettingsOpen: false });
  }

  renderCanvas(widgets) {
    if (widgets.length === 0) {
      return (
        <ThemeProvider theme={theme}>
          <div style={{ paddingLeft: '64px' }}>
            <ReorderWidgetsDialog
              open={this.state.orderDialogOpen}
              onClose={() => this.setState({ orderDialogOpen: false })}
              onChange={newOrder => this.handleReorder(newOrder)}
              widgets={this.state.page.widgets}
            />

            <PageSettingsDialog
              open={this.state.pageSettingsOpen}
              onClose={() => this.setState({ pageSettingsOpen: false })}
              onSave={settings => this.handleUpdateSettings(settings)}
              page={this.state.page}
            />

            <Loading $active={this.state.isLoading}>Saving...</Loading>

            <AdminSidebar
              isPreview={this.state.sitePreview}
              onSave={() => this.handleSave()}
              onPreview={() => this.handleSitePreview()}
              onAddWidget={widget => this.handleAddWidget(widget)}
              onWidgetReorderDialogOpen={() => this.handleReorderDialogOpen()}
              onPageSettingsOpen={() => this.handlePageSettingsOpen()}
            />

            <EmptyCanvas>
              <div>
                <Title $primary>It's always good to start with a clean slate. </Title>
                <Text>Use the sidebar to add content. </Text>
              </div>
            </EmptyCanvas>
          </div>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider theme={theme}>
        <div style={{ paddingLeft: '64px' }}>
          <ReorderWidgetsDialog
            open={this.state.orderDialogOpen}
            onClose={() => this.setState({ orderDialogOpen: false })}
            onChange={newOrder => this.handleReorder(newOrder)}
            widgets={this.state.page.widgets}
          />

          <PageSettingsDialog
            open={this.state.pageSettingsOpen}
            onClose={() => this.setState({ pageSettingsOpen: false })}
            onSave={settings => this.handleUpdateSettings(settings)}
            page={this.state.page}
          />

          <Loading active={this.state.isLoading}>Saving...</Loading>

          <AdminSidebar
            isPreview={this.state.sitePreview}
            onSave={() => this.handleSave()}
            onPreview={() => this.handleSitePreview()}
            onAddWidget={widget => this.handleAddWidget(widget)}
            onWidgetReorderDialogOpen={() => this.handleReorderDialogOpen()}
            onPageSettingsOpen={() => this.handlePageSettingsOpen()}
          />

          <div className="admin-canvas-widgets">
            <ReactSortable
              list={widgets.map(w => ({ ...w, id: w.widgetId }))}
              setList={newWidgets => this.handleReorder(newWidgets)}
              handle=".btn-drag"
              animation={150}
              tag="div"
            >
              {widgets.map((widget) =>
                <div key={widget.widgetId}>
                  <AdminWidget
                    isPreview={this.state.sitePreview}
                    widgetId={widget.widgetId}
                    widgetComponent={widget.component}
                    onUpdate={(widgetId, settings) => this.handleUpdate(widgetId, settings)}
                    onDelete={widgetId => this.handleDelete(widgetId)}
                    {...widget.settings}
                  />
                </div>)}
            </ReactSortable>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  render() {
    return (
      <div>
        {this.renderCanvas(this.state.page.widgets)}
      </div>
    );
  }
}

PageBuilder.propTypes = {
  admin: PropTypes.bool.isRequired,
  page: PropTypes.object.isRequired,
  save_url: PropTypes.string.isRequired,
};

export default PageBuilder;
